import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronUp, ChevronDown, X, Search, User, GripVertical } from 'lucide-react';
import PhotoUpload from './PhotoUpload';
import { useSiteData } from '@/context/SiteDataContext';

// -----------------------------------------------------------------------------
// Standard positions available in every department. Admin may create additional
// custom positions on-the-fly (stored per-department).
// -----------------------------------------------------------------------------
const STD_POSITIONS = ['President', 'Vice President', 'Cabinet'];

const emptyDept = () => ({
  name: '', tagline: '', motto: '', image: '', logo: '',
  description: '', mission: '', vision: '', values: '',
  gallery_tag: '', contact_email: '', contact_phone: '',
  meeting_time: '', meeting_location: '',
  assignments: [], custom_positions: [], order: 999,
});

export default function DepartmentsEditor() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const { refresh } = useSiteData();

  const load = async () => {
    try {
      const { data } = await api.get('/site/departments');
      setItems(data);
    } catch { toast.error('Failed to load departments'); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const payload = { ...editing };
      // Normalise assignments: sort by order and reindex.
      payload.assignments = (payload.assignments || [])
        .filter(a => a.leader_id)
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
        .map((a, i) => ({ ...a, order: i }));
      if (editing.id) await api.patch(`/site/departments/${editing.id}`, payload);
      else await api.post('/site/departments', payload);
      toast.success('Saved · changes are live');
      setEditing(null);
      await load();
      // Refresh both departments and leaders (assignments join to leaders on public site).
      refresh?.(['departments', 'leaders']);
    } catch { toast.error('Save failed'); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this department? (Leader profiles remain untouched.)')) return;
    await api.delete(`/site/departments/${id}`);
    toast.success('Deleted');
    await load();
    refresh?.(['departments']);
  };

  const reorder = async (id, delta) => {
    const idx = items.findIndex(i => i.id === id);
    const swapIdx = idx + delta;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    const a = items[idx], b = items[swapIdx];
    await Promise.all([
      api.patch(`/site/departments/${a.id}`, { order: b.order }),
      api.patch(`/site/departments/${b.id}`, { order: a.order }),
    ]);
    await load();
    refresh?.(['departments']);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="font-serif text-xl text-royal font-bold">Departments ({items.length})</h3>
          <p className="text-xs text-charcoal-muted mt-1">Assign existing leaders as President, VP, Cabinet, or a custom position. Changes appear on the public department page immediately.</p>
        </div>
        <button onClick={() => setEditing(emptyDept())} data-testid="departments-add-btn"
          className="btn-royal text-sm py-2 px-4 flex items-center gap-1"><Plus className="w-4 h-4" /> Add Department</button>
      </div>

      <div className="grid gap-3">
        {items.map((d, i) => {
          const count = (d.assignments || []).length;
          return (
            <div key={d.id} className="bg-white border rounded-lg p-3 flex items-center gap-3">
              {d.image && <img src={d.image} alt="" className="w-14 h-14 object-cover rounded flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-royal truncate">{d.name}</div>
                <div className="text-xs text-charcoal-muted truncate">
                  {d.tagline || d.mission || '—'}
                </div>
                <div className="text-[11px] text-gold uppercase tracking-widest mt-0.5">
                  {count} {count === 1 ? 'leader assigned' : 'leaders assigned'}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => reorder(d.id, -1)} disabled={i === 0} className="p-2 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => reorder(d.id, 1)} disabled={i === items.length - 1} className="p-2 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                <button onClick={() => setEditing({ ...emptyDept(), ...d })}
                  data-testid={`dept-edit-${d.id}`}
                  className="text-xs px-3 py-1.5 bg-royal text-white rounded">Edit</button>
                <button onClick={() => remove(d.id)} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
        {!items.length && <div className="text-sm text-charcoal-muted">No departments yet.</div>}
      </div>

      {editing && (
        <DepartmentModal
          value={editing}
          onChange={setEditing}
          onClose={() => setEditing(null)}
          onSave={save}
          busy={busy}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Modal editor with tabs for Details vs Leadership Assignments.
// -----------------------------------------------------------------------------

function DepartmentModal({ value, onChange, onClose, onSave, busy }) {
  const [tab, setTab] = useState('details');
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-3xl w-full my-8 max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-serif text-xl text-royal font-bold">{value.id ? 'Edit' : 'Add'} Department</h3>
            <div className="flex gap-1 mt-3">
              <TabBtn active={tab === 'details'} onClick={() => setTab('details')} testid="dept-tab-details">Details</TabBtn>
              <TabBtn active={tab === 'assignments'} onClick={() => setTab('assignments')} testid="dept-tab-assignments">
                Leadership {value.assignments?.length ? `(${value.assignments.length})` : ''}
              </TabBtn>
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        {tab === 'details' && (
          <div className="p-6 space-y-4">
            <Two>
              <F label="Name"><input value={value.name || ''} onChange={(e) => set('name', e.target.value)} className="w-full px-3 py-2 border rounded" data-testid="dept-name-input" /></F>
              <F label="Tagline"><input value={value.tagline || ''} onChange={(e) => set('tagline', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
            </Two>
            <F label="Motto (bold quoted line on hero)"><input value={value.motto || ''} onChange={(e) => set('motto', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
            <Two>
              <PhotoUpload label="Cover / Hero image" value={value.image} onChange={(v) => set('image', v)} category="departments" />
              <PhotoUpload label="Department logo (round)" value={value.logo} onChange={(v) => set('logo', v)} category="departments" />
            </Two>
            <F label="Description / About"><textarea rows="5" value={value.description || ''} onChange={(e) => set('description', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
            <F label="Mission"><textarea rows="2" value={value.mission || ''} onChange={(e) => set('mission', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
            <F label="Vision"><textarea rows="2" value={value.vision || ''} onChange={(e) => set('vision', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
            <F label="Core Values (one per line)"><textarea rows="3" value={value.values || ''} onChange={(e) => set('values', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
            <F label="Gallery filter tag (matches gallery caption/tag)"><input value={value.gallery_tag || ''} onChange={(e) => set('gallery_tag', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
            <Two>
              <F label="Contact email"><input value={value.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
              <F label="Contact phone"><input value={value.contact_phone || ''} onChange={(e) => set('contact_phone', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
              <F label="Meeting time / cadence"><input value={value.meeting_time || ''} onChange={(e) => set('meeting_time', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
              <F label="Meeting location"><input value={value.meeting_location || ''} onChange={(e) => set('meeting_location', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
            </Two>
          </div>
        )}

        {tab === 'assignments' && (
          <AssignmentsPanel
            value={value}
            onChange={onChange}
          />
        )}

        <div className="p-6 border-t flex justify-end gap-3 sticky bottom-0 bg-white z-10">
          <button onClick={onClose} className="px-4 py-2 text-charcoal-muted">Cancel</button>
          <button onClick={onSave} disabled={busy} data-testid="dept-save-btn" className="btn-royal">
            {busy ? 'Saving…' : 'Save · Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Assignments UI — leader picker, position dropdown, reorder, remove.
// -----------------------------------------------------------------------------

function AssignmentsPanel({ value, onChange }) {
  const [leaders, setLeaders] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => { api.get('/site/leaders').then(r => setLeaders(r.data || [])).catch(() => {}); }, []);

  const assignments = value.assignments || [];
  const customPositions = value.custom_positions || [];
  const allPositions = [...STD_POSITIONS, ...customPositions];

  const setAssignments = (next) => onChange({ ...value, assignments: next });
  const setCustomPositions = (next) => onChange({ ...value, custom_positions: next });

  const leadersById = useMemo(() => Object.fromEntries((leaders || []).map(l => [l.id, l])), [leaders]);
  const assignedIds = new Set(assignments.map(a => a.leader_id));
  const availableLeaders = leaders.filter(l => !assignedIds.has(l.id));

  const addLeader = (leader, position) => {
    const nextOrder = assignments.length
      ? Math.max(...assignments.map(a => a.order ?? 0)) + 1
      : 0;
    setAssignments([...assignments, { leader_id: leader.id, position, order: nextOrder }]);
    setPickerOpen(false);
  };

  const updateAssignment = (idx, patch) => {
    const next = assignments.map((a, i) => i === idx ? { ...a, ...patch } : a);
    setAssignments(next);
  };

  const removeAssignment = (idx) => {
    setAssignments(assignments.filter((_, i) => i !== idx));
  };

  const move = (idx, delta) => {
    const swap = idx + delta;
    if (swap < 0 || swap >= assignments.length) return;
    const next = [...assignments];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setAssignments(next.map((a, i) => ({ ...a, order: i })));
  };

  const addCustomPosition = () => {
    const name = window.prompt('New position name (e.g. "Secretary", "Treasurer", "Chaplain")');
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    if (allPositions.some(p => p.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('That position already exists');
      return;
    }
    setCustomPositions([...customPositions, trimmed]);
    toast.success(`Position "${trimmed}" added — assign a leader below`);
  };

  return (
    <div className="p-6 space-y-5">
      <div className="bg-royal/5 border-l-4 border-gold p-4 rounded text-sm text-charcoal-muted">
        <div className="font-semibold text-royal mb-1">How this works</div>
        Add leaders from your <strong>Leadership directory</strong> here — no duplicate profiles are created.
        Assign any position (President, Vice President, Cabinet, or a <em>custom</em> one you create), reorder,
        change titles, or remove. Everything appears on this department&rsquo;s public page instantly on save.
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setPickerOpen(true)} data-testid="dept-add-leader-btn"
          className="btn-royal text-sm py-2 px-4 flex items-center gap-1">
          <Plus className="w-4 h-4" /> Add Leader
        </button>
        <button onClick={addCustomPosition} data-testid="dept-add-position-btn"
          className="text-sm py-2 px-4 border border-royal text-royal rounded-full hover:bg-royal hover:text-white transition flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Create Custom Position
        </button>
      </div>

      {customPositions.length > 0 && (
        <div className="text-xs text-charcoal-muted">
          <span className="uppercase tracking-widest font-semibold text-royal">Custom positions for this dept:</span>{' '}
          {customPositions.map((p, i) => (
            <span key={p} className="inline-flex items-center gap-1 ml-1 bg-gold/20 text-royal-dark px-2 py-0.5 rounded-full">
              {p}
              <button title="Remove" onClick={() => {
                if (!window.confirm(`Remove position "${p}"? Any leaders using it will need re-assigning.`)) return;
                setCustomPositions(customPositions.filter((_, j) => j !== i));
              }} className="hover:text-red-600"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {assignments.length === 0 && (
          <div className="text-sm text-charcoal-muted bg-white border rounded-lg p-6 text-center">
            No leaders assigned yet. Click <strong>Add Leader</strong> to select from the Leadership directory.
          </div>
        )}
        {assignments.map((a, i) => {
          const leader = leadersById[a.leader_id];
          const isCustom = !STD_POSITIONS.includes(a.position);
          return (
            <div key={`${a.leader_id}-${i}`} data-testid={`assignment-row-${i}`}
                 className="bg-white border rounded-lg p-3 flex items-center gap-3">
              <div className="flex flex-col gap-0.5 shrink-0">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30" title="Move up"><ChevronUp className="w-3.5 h-3.5" /></button>
                <GripVertical className="w-3.5 h-3.5 text-charcoal-muted mx-auto" />
                <button onClick={() => move(i, 1)} disabled={i === assignments.length - 1} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30" title="Move down"><ChevronDown className="w-3.5 h-3.5" /></button>
              </div>
              <div className="w-12 h-12 rounded overflow-hidden bg-royal/10 shrink-0 flex items-center justify-center">
                {leader?.photo
                  ? <img src={leader.photo} alt="" className="w-full h-full object-cover"
                        style={{ objectPosition: leader.photoPosition || 'center top' }} />
                  : <User className="w-5 h-5 text-royal" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-royal truncate">
                  {leader?.name || <span className="text-red-600 italic">Leader missing — was deleted?</span>}
                </div>
                <div className="text-xs text-charcoal-muted truncate">{leader?.role || ''}</div>
              </div>
              <div className="min-w-[180px]">
                <select
                  value={isCustom ? '__custom' : a.position}
                  onChange={(e) => {
                    if (e.target.value === '__custom') {
                      const custom = window.prompt('Enter custom position title (e.g. "Chaplain"):', a.position && isCustom ? a.position : '');
                      if (custom && custom.trim()) {
                        const trimmed = custom.trim();
                        if (!customPositions.includes(trimmed) && !STD_POSITIONS.includes(trimmed)) {
                          setCustomPositions([...customPositions, trimmed]);
                        }
                        updateAssignment(i, { position: trimmed });
                      }
                    } else {
                      updateAssignment(i, { position: e.target.value });
                    }
                  }}
                  data-testid={`assignment-position-${i}`}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  {allPositions.map(p => <option key={p} value={p}>{p}</option>)}
                  <option value="__custom">➕ Add new custom…</option>
                  {isCustom && <option value={a.position}>{a.position}</option>}
                </select>
              </div>
              <button onClick={() => removeAssignment(i)}
                data-testid={`assignment-remove-${i}`}
                className="p-2 text-red-500 hover:bg-red-50 rounded shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          );
        })}
      </div>

      {pickerOpen && (
        <LeaderPickerModal
          leaders={availableLeaders}
          positions={allPositions}
          onCancel={() => setPickerOpen(false)}
          onPick={addLeader}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Leader picker: searchable list + position select.
// -----------------------------------------------------------------------------

function LeaderPickerModal({ leaders, positions, onCancel, onPick }) {
  const [q, setQ] = useState('');
  const [position, setPosition] = useState(positions[2] || positions[0] || 'Cabinet');
  const filtered = leaders.filter(l => {
    const s = `${l.name || ''} ${l.role || ''} ${l.section || ''}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-xl w-full max-h-[80vh] flex flex-col overflow-hidden">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h4 className="font-serif text-lg text-royal font-bold">Add a Leader</h4>
            <p className="text-xs text-charcoal-muted mt-0.5">Pick from the existing Leadership directory — no duplicates.</p>
          </div>
          <button onClick={onCancel}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-charcoal-muted" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, role, or section…"
              data-testid="leader-picker-search"
              className="w-full pl-9 pr-3 py-2 border rounded" />
          </div>
          <div>
            <div className="text-xs font-semibold text-royal mb-1">Assign as</div>
            <select value={position} onChange={(e) => setPosition(e.target.value)}
              data-testid="leader-picker-position"
              className="w-full px-3 py-2 border rounded text-sm">
              {positions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-charcoal-muted">
              {leaders.length === 0
                ? 'Every leader is already assigned. Create a new leader in the Leadership tab first.'
                : 'No leaders match that search.'}
            </div>
          )}
          {filtered.map(l => (
            <button key={l.id} onClick={() => onPick(l, position)}
              data-testid={`leader-picker-item-${l.id}`}
              className="w-full flex items-center gap-3 p-2 rounded hover:bg-royal/5 text-left">
              <div className="w-10 h-10 rounded overflow-hidden bg-royal/10 shrink-0 flex items-center justify-center">
                {l.photo
                  ? <img src={l.photo} alt="" className="w-full h-full object-cover" style={{ objectPosition: l.photoPosition || 'center top' }} />
                  : <User className="w-4 h-4 text-royal" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-royal text-sm truncate">{l.name}</div>
                <div className="text-xs text-charcoal-muted truncate">{l.role}</div>
              </div>
              <div className="text-xs text-gold font-bold">Add</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Small layout helpers
// -----------------------------------------------------------------------------
function TabBtn({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-xs uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full transition ${
        active ? 'bg-royal text-white' : 'bg-white text-royal border border-royal/30 hover:border-royal'
      }`}>{children}</button>
  );
}
function F({ label, children }) { return (<div><div className="text-sm font-semibold text-royal mb-1">{label}</div>{children}</div>); }
function Two({ children }) { return <div className="grid md:grid-cols-2 gap-4">{children}</div>; }
